import { useState, useEffect, useRef } from 'react';
import { ListPlus, Plus, Play, ListMusic, Lock, ChevronRight, Check, Loader2 } from 'lucide-react';
import { useQueue } from '../contexts/QueueContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TrackActions = ({ track, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [addedTo, setAddedTo] = useState(new Set());
  const menuRef = useRef(null);
  const { addToQueue, playNext } = useQueue();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const fetchPlaylists = async () => {
    if (!isAuthenticated) return;
    setLoadingPlaylists(true);
    try {
      const res = await axios.get(`${API}/api/playlists`, { withCredentials: true });
      setPlaylists(res.data.playlists || []);
    } catch {} finally { setLoadingPlaylists(false); }
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open && isAuthenticated) fetchPlaylists();
  };

  const handleAddToQueue = () => {
    addToQueue(track);
    toast.success('Added to queue');
    setOpen(false);
  };

  const handlePlayNext = () => {
    playNext(track);
    toast.success('Playing next');
    setOpen(false);
  };

  const handleAddToPlaylist = async (playlistId) => {
    try {
      const isYouTube = track.track_type === 'youtube' || track.youtube_id;
      await axios.post(`${API}/api/playlists/${playlistId}/tracks`, {
        track_id: track.id || track.track_id || track.youtube_id,
        track_type: isYouTube ? 'youtube' : (track.synced_from_hearthis || track.hearthis_url ? 'mixtape' : 'release'),
        title: track.title || track.name || '',
        artist: track.dj || track.artist || track.channel_title || '',
        cover: track.cover || track.image || track.thumbnail || '',
        duration: track.duration || 0,
        youtube_id: track.youtube_id || undefined,
        url: track.url || undefined,
      }, { withCredentials: true });
      setAddedTo(prev => new Set([...prev, playlistId]));
      toast.success('Added to playlist');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.post(`${API}/api/playlists`, { name: newName.trim() }, { withCredentials: true });
      const newPlaylist = res.data;
      await handleAddToPlaylist(newPlaylist.id);
      setCreatingNew(false);
      setNewName('');
      fetchPlaylists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={handleOpen}
        data-testid={`track-actions-${track.id || track.track_id}`}
        className={`flex items-center gap-1.5 transition-all ${
          compact
            ? 'p-1.5 rounded-sm hover:bg-white/10'
            : 'px-3 py-2 text-sm border border-white/20 rounded-sm hover:border-primary hover:text-primary'
        }`}
        title="Add to playlist or queue"
      >
        <ListPlus className={compact ? 'w-4 h-4' : 'w-4 h-4'} />
        {!compact && <span>Add to</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-64 bg-zinc-900 border border-white/10 rounded-sm shadow-2xl z-50 overflow-hidden"
          data-testid="track-actions-menu"
        >
          {/* Queue Actions */}
          <button
            onClick={handlePlayNext}
            data-testid="play-next-btn"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-white/5 transition-colors text-left"
          >
            <Play className="w-4 h-4 text-primary" /> Play Next
          </button>
          <button
            onClick={handleAddToQueue}
            data-testid="add-to-queue-btn"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-white/5 transition-colors text-left border-b border-white/5"
          >
            <ListMusic className="w-4 h-4 text-primary" /> Add to Queue
          </button>

          {/* Playlist Section */}
          {!isAuthenticated ? (
            <div className="px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
              <Lock className="w-3 h-3" /> Login to save to playlists
            </div>
          ) : (
            <>
              <div className="px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider font-bold">
                Playlists
              </div>
              {loadingPlaylists ? (
                <div className="px-4 py-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto">
                  {playlists.map(pl => (
                    <button
                      key={pl.id}
                      onClick={() => handleAddToPlaylist(pl.id)}
                      disabled={addedTo.has(pl.id)}
                      data-testid={`add-to-playlist-${pl.id}`}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-white/5 transition-colors text-left disabled:opacity-50"
                    >
                      <span className="truncate">{pl.name}</span>
                      {addedTo.has(pl.id) ? <Check className="w-4 h-4 text-green-400" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                    </button>
                  ))}
                </div>
              )}
              {/* Create New */}
              {creatingNew ? (
                <div className="p-2 border-t border-white/5">
                  <input
                    autoFocus
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateAndAdd()}
                    placeholder="Playlist name..."
                    className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    data-testid="new-playlist-input"
                  />
                  <div className="flex gap-2 mt-2">
                    <button onClick={handleCreateAndAdd} className="flex-1 text-xs bg-primary text-black py-1.5 rounded-sm font-bold hover:bg-primary/90">Create & Add</button>
                    <button onClick={() => setCreatingNew(false)} className="text-xs px-3 py-1.5 border border-white/10 rounded-sm hover:bg-white/5">Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setCreatingNew(true)}
                  data-testid="create-playlist-btn"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-white/5 transition-colors text-left border-t border-white/5"
                >
                  <Plus className="w-4 h-4 text-primary" /> New Playlist
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TrackActions;
